import {
    DEFAULT_IMAGE_RATIO,
    DEFAULT_IMAGE_SIZE,
    IMAGE_RATIO_RESOLUTIONS,
    ImageModel,
    ImageRatioModel,
    ImageRatios,
    ImageSizes,
} from './models/ImageModel';
import { IMAGE_CDN_URL } from '../../../constants/GlobalConstants';
import { ImageFormats } from '../../../constants/Enums';
import { VideoModel } from '../video/models/VideoModel';
import { getMainImageFromMainMedia } from '../mainMedia/MainMedia';

export const getImageByCropRatio = (
    image: ImageModel | null,
    ratio: ImageRatios = DEFAULT_IMAGE_RATIO,
    size: ImageSizes = DEFAULT_IMAGE_SIZE,
    getWebP: boolean = false,
) => {
    if (!image) {
        return '';
    }

    const dimensions = getImageWidthByRatioAndSize(ratio, size);
    const ratioConfig = getImageRatioConfig(image, ratio);

    let imgSrc = `${IMAGE_CDN_URL}/process/${image.path}?operations=autocrop(${dimensions.width}:${dimensions.height})`;
    if (hasImageRatios(ratioConfig)) {
        imgSrc = `${IMAGE_CDN_URL}/process/${image.path}?operations=crop(${ratioConfig?.top}:${ratioConfig?.left}:${ratioConfig?.bottom}:${ratioConfig?.right}),fit(${dimensions.width}:${dimensions.height})`;
    }

    if (getWebP) {
        imgSrc += `&format=${ImageFormats.WEBP}`;
    }

    return imgSrc;
};

export const getMainMediaImageByCropRatio = (
    mainMedia: (string | ImageModel | VideoModel | null)[],
    ratio: ImageRatios = DEFAULT_IMAGE_RATIO,
    size: ImageSizes = DEFAULT_IMAGE_SIZE,
    getWebP: boolean = false,
): string => {
    const image = getMainImageFromMainMedia(mainMedia);
    if (!image) {
        return '';
    }

    return getImageByCropRatio(image, ratio, size, getWebP);
};

export const overrideImageAutoCrop = (
    url: string,
    ratio: ImageRatios = DEFAULT_IMAGE_RATIO,
    size: ImageSizes = DEFAULT_IMAGE_SIZE,
) => {
    const dimensions = getImageWidthByRatioAndSize(ratio, size);
    const crop = `?operations=autocrop(${dimensions.width}:${dimensions.height})`;

    if (url.includes('?')) {
        return url.replace(/\?.*\)/g, '') + crop;
    }

    return url + crop;
};

const hasImageRatios = (ratio: ImageRatioModel | null): boolean => {
    if (ratio) {
        return ratio.top !== '0' || ratio.bottom !== '0' || ratio.left !== '0' || ratio.right !== '0';
    }

    return false;
};

const getImageRatioConfig = (image: ImageModel, ratio: ImageRatios): ImageRatioModel | null => {
    if (image.urls === null) {
        return null;
    }

    switch (ratio) {
        case ImageRatios.RATIO_1X1: {
            return image.urls?.ratio1x1;
        }
        case ImageRatios.RATIO_2X3: {
            return image.urls?.ratio2x3;
        }
        case ImageRatios.RATIO_3X2: {
            return image.urls?.ratio3x2;
        }
        case ImageRatios.RATIO_4X3: {
            return image.urls?.ratio4x3;
        }
        case ImageRatios.RATIO_3X4: {
            return image.urls?.ratio3x4;
        }
        case ImageRatios.RATIO_16X9: {
            return image.urls?.ratio16x9;
        }
        case ImageRatios.RATIO_9X16: {
            return image.urls?.ratio9x16;
        }
        default: {
            return image.urls?.ratio16x9;
        }
    }
};

const getImageWidthByRatioAndSize = (ratio: ImageRatios = ImageRatios.RATIO_4X3, size: ImageSizes = ImageSizes.MID) => {
    return IMAGE_RATIO_RESOLUTIONS[`${size}_${ratio}`] ?? { width: '640', height: '360' };
};
